/**
 * Which tool calls are in flight right now.
 *
 * WHY THIS IS NOT A SINGLE `activeTool`. pi-agent-core runs a turn's tool calls
 * in PARALLEL by default, and it emits every `tool_execution_start` up front
 * before awaiting any of them — so a three-tool batch arrives as three
 * `tool_start` events, then three `tool_end` events in COMPLETION order. Code
 * that keeps one tool and clears it on any `tool_end` therefore shows only the
 * last tool of the batch, and drops back to the thinking dots the moment the
 * FASTEST one returns, while the other two are still running. That was the
 * reported "the indicator flickers past and goes back to dots".
 *
 * `toolCallId` has always been on the wire; this keys on it.
 *
 * The second half is `linger`. Even correctly tracked, a tool that returns in
 * 150ms (a desk update, a date lookup) paints its bubble and erases it inside
 * three frames, which reads as a glitch rather than as information. A tool that
 * ends before MIN_TOOL_DISPLAY_MS stays on screen for the remainder — but only
 * while nothing else is running, so it never masks live work.
 */

export interface RunningTool {
  readonly toolCallId: string;
  readonly name: string;
  /** The model's own pre-tool narration, when it narrated before calling. */
  readonly narration?: string;
  /** True when the call's args identify it as a read (a desk 'list'/'read') —
   *  picks the read-flavored fallback label over the write-flavored one.
   *  Absent for restored entries (a history dump carries no args). */
  readonly readOnly?: boolean;
  /** Client receipt time of `tool_start` — drives the elapsed counter. */
  readonly startedAt: number;
  /** Rebuilt from a `history` dump rather than seen live (a reconnect landed
   *  mid-call). Such an entry stands in for a call whose id we may not have:
   *  the gateway sends one, but a runtime older than this change does not, and
   *  a reconnect mid-BATCH only ever hears about one of the calls. `endTool`
   *  uses it as the fallback match so a placeholder can never outlive the turn.
   */
  readonly restored?: boolean;
}

/** How long a tool bubble stays up even if the call returns sooner. Long
 *  enough to read three or four words, short enough that a fast tool doesn't
 *  hold up the next one. */
export const MIN_TOOL_DISPLAY_MS = 700;

/** Synthetic id for a call restored from a `history` dump that carried no
 *  `toolCallId` — a runtime older than the change that started sending one.
 *  Prefixed so consumers that track ids as bare strings (ws-context's mode
 *  tracking) can recognise a placeholder without carrying the full state. */
export const RESTORED_PREFIX = "restored:";
export const restoredToolCallId = (toolName: string): string => `${RESTORED_PREFIX}${toolName}`;

/** The desk tool's read-only actions — mirror of the action union in
 *  apps/runtime/src/tools/desk.ts. One tool covers reads and writes, and the
 *  static fallback label says "Updating the desk", which reads as a mutation
 *  when the agent is merely looking something up. Everything not listed here
 *  (create/update/delete/dispatch/…) writes, and an action added later falls
 *  to the write label — the safe direction to be wrong in. */
const DESK_READ_ACTIONS = new Set(["read", "list", "search", "schemas"]);

/** The desk actions that modify the item named by `item_id` — what the desk
 *  announces and follows. An allowlist, the opposite of the read list above:
 *  missing a new action means one change goes unannounced, while guessing
 *  wrong the other way would move a following view on a read. `delete` is
 *  absent on purpose (there is nothing left to look at); `create` carries no
 *  item_id and arrives as desk_item_created instead. */
const DESK_CHANGE_ACTIONS = new Set(["update", "dispatch", "bulk_patch", "bulk_update", "restore"]);

const deskAction = (toolName: string, args: unknown): string | null =>
  toolName === "desk"
  && typeof args === "object" && args !== null
  && "action" in args && typeof args.action === "string"
    ? args.action
    : null;

/** Whether a tool call's args identify it as read-only. Desk-only today. */
export const isReadOnlyToolCall = (toolName: string, args: unknown): boolean =>
  DESK_READ_ACTIONS.has(deskAction(toolName, args) ?? "");

/** Whether a tool call changes the desk item its args name. */
export const isDeskChangeCall = (toolName: string, args: unknown): boolean =>
  DESK_CHANGE_ACTIONS.has(deskAction(toolName, args) ?? "");

/** The deploy actions after which the app's desk entry shows something new —
 *  what the desk announces and follows for an app. The same allowlist shape
 *  as the desk actions above. `undeploy` is absent (nothing left to show);
 *  `status` and `check_domain` are reads. */
const APP_CHANGE_ACTIONS = new Set(["deploy", "set_public", "set_pwa", "set_login", "move", "add_domain", "remove_domain"]);

/** The app a deploy tool call changes, by slug — null for a read, another
 *  tool, or a call that names no app. A deploy is announced from its own
 *  call, never through a desk row: the deployments row the tool also writes
 *  is subject to the desk read's cap and absent from exactly the desks an
 *  app has filled. */
export const appChangedBy = (toolName: string, args: unknown): string | null => {
  if (toolName !== "deploy" || typeof args !== "object" || args === null) return null;
  const { action, slug } = args as { action?: unknown; slug?: unknown };
  return typeof action === "string" && APP_CHANGE_ACTIONS.has(action) && typeof slug === "string" && slug.length > 0
    ? slug
    : null;
};

export interface ToolActivityState {
  /** In start order, so the oldest — the one the user has already read — is
   *  the one that stays on screen while the rest of a batch churns. */
  readonly running: readonly RunningTool[];
  /** A finished tool still serving out MIN_TOOL_DISPLAY_MS. Never set while
   *  anything is actually running. */
  readonly linger: RunningTool | null;
}

export const EMPTY_TOOL_ACTIVITY: ToolActivityState = { running: [], linger: null };

export const startTool = (
  state: ToolActivityState,
  tool: RunningTool,
): ToolActivityState => ({
  // Re-issued ids replace rather than duplicate; live work always beats a
  // lingering finished one.
  running: [...state.running.filter((t) => t.toolCallId !== tool.toolCallId), tool],
  linger: null,
});

export const endTool = (
  state: ToolActivityState,
  toolCallId: string,
  now: number,
): ToolActivityState => {
  const ended = state.running.find((t) => t.toolCallId === toolCallId)
    ?? state.running.find((t) => t.restored);
  // An id we never saw start (a duplicate end, or the other half of a batch we
  // reconnected into) must not clear the bubble — that is the exact bug this
  // module exists to fix.
  if (!ended) return state;
  const running = state.running.filter((t) => t.toolCallId !== ended.toolCallId);
  if (running.length > 0) return { running, linger: null };
  const shortLived = now - ended.startedAt < MIN_TOOL_DISPLAY_MS;
  return { running, linger: shortLived ? ended : null };
};

export const expireLinger = (state: ToolActivityState): ToolActivityState =>
  state.linger === null ? state : { ...state, linger: null };

/** Milliseconds until `linger` should be dropped, or null when nothing is
 *  lingering. The provider arms one timer from this. */
export const lingerExpiresInMs = (state: ToolActivityState, now: number): number | null =>
  state.linger === null
    ? null
    : Math.max(0, state.linger.startedAt + MIN_TOOL_DISPLAY_MS - now);

/** The tool the indicator names: the longest-running call, or the one still
 *  serving out its minimum. Null when the agent isn't inside a tool. */
export const primaryTool = (state: ToolActivityState): RunningTool | null =>
  state.running[0] ?? state.linger ?? null;

/** Running calls the bubble is NOT naming, for the "+2 more" marker. */
export const hiddenToolCount = (state: ToolActivityState): number =>
  Math.max(0, state.running.length - 1);
